export const audienceBenefitOrder = ["players", "creators", "influencers", "collections"];

export const audienceBenefitPages = {
  players: {
    slug: "players",
    path: "/players",
    shortLabel: "Players",
    footerLabel: "Players",
    railText: "Campaign while a market is live.",
    eyebrow: "For players",
    title: "Pick a side and rally your people.",
    description:
      "RaizIQ is not a passive prediction market where you make a guess and wait around. Pick a side, bring your group, and watch every vote move the board in real time.",
    primaryCta: { label: "Enter the live board", to: "/markets" },
    secondaryCta: { label: "See how voting works", to: "/faq" },
    intro: {
      eyebrow: "Active participation",
      title: "The market is a live activity.",
      body:
        "Players can make the case, host the Space, go live, post updates, and bring people in while the board is still moving."
    },
    benefits: [
      {
        title: "Campaign while it is live",
        body: "Pick a side and keep working while votes can still change the result."
      },
      {
        title: "Bring your group",
        body: "Use X Spaces, livestreams, threads, group chats, and community pushes to get people to vote."
      },
      {
        title: "Watch momentum move",
        body: "Live totals, vote history, and comments show whether your side is gaining ground."
      },
      {
        title: "Build visible reputation",
        body: "Leaderboards and profile activity make it clear who shows up and who can move markets."
      }
    ],
    activityTitle: "Ways players make the board move",
    activities: [
      "Host an X Space for your side.",
      "Run a livestream while the vote total moves.",
      "Bring a holder chat, gaming squad, or group chat into the market.",
      "Post updates when the other side starts gaining ground.",
      "Celebrate leaderboard movement and top voters."
    ],
    proof: {
      title: "Your effort shows up publicly.",
      body:
        "Every confirmed vote changes the public board. The vote history, live totals, comments, and leaderboards show the campaign instead of hiding it inside private engagement metrics."
    },
    fee: null,
    finalCta: {
      title: "Find a market that needs your side.",
      body: "Scan the live board, pick a side, and give your people something active to do.",
      primary: { label: "Browse markets", to: "/markets" },
      secondary: { label: "View leaderboards", to: "/leaderboards" }
    }
  },
  creators: {
    slug: "creators",
    path: "/creators",
    shortLabel: "Creators",
    footerLabel: "Creators",
    railText: "Launch the market people rally around.",
    eyebrow: "For creators",
    title: "Launch the board people rally around.",
    description:
      "Create the market, frame the sides, set the window, and give your audience a clear action. In the current vote flow, the market creator wallet receives 3% of each vote amount in the vote transaction.",
    primaryCta: { label: "Create a market", to: "/create" },
    secondaryCta: { label: "Browse live examples", to: "/markets" },
    intro: {
      eyebrow: "Market creation",
      title: "Give people a clear side to back.",
      body:
        "A participation market turns a debate, drop, release, challenge, or matchup into a page with live totals, share links, social proof, comments, and leaderboard activity."
    },
    benefits: [
      {
        title: "Frame the moment",
        body: "Choose the question, write the context, set the window, and define the answer options."
      },
      {
        title: "Create a campaign page",
        body: "Send people to one market page instead of scattering the action across posts and replies."
      },
      {
        title: "Receive the creator fee",
        body: "The market creator wallet currently receives 3% of each vote amount in the vote transaction."
      },
      {
        title: "Learn what people back",
        body: "Votes show what the audience actually rallies behind while the market is live."
      }
    ],
    activityTitle: "Markets creators can launch",
    activities: [
      "Launch a market around a debate, drop, release, challenge, matchup, or event.",
      "Send followers to a single market page before or during a live show.",
      "Use vote movement as the show format.",
      "React to leaderboard shifts and top voters while the campaign runs."
    ],
    proof: {
      title: "The market carries the receipts.",
      body:
        "The market page shows the prompt, options, totals, vote history, comments, and social proof so people can understand what happened after the campaign ends."
    },
    fee: {
      eyebrow: "Creator fee",
      title: "3% creator fee on each vote in markets you create.",
      body:
        "For markets you create, the current vote flow routes 3% of each vote amount to the market creator wallet in the vote transaction. Winner settlement uses the remaining escrow after fee lanes.",
      notes: [
        "The market creator wallet currently receives 3% of each vote amount in the vote transaction.",
        "The creator fee applies to markets created by that creator and is separate from winner settlement.",
        "Creator fee routing is tied to the current vote flow and service terms."
      ]
    },
    finalCta: {
      title: "Launch the next rally point.",
      body: "Create a market with a sharp question, direct options, and a voting window people can act on.",
      primary: { label: "Create a market", to: "/create" },
      secondary: { label: "Review how it works", to: "/faq" }
    }
  },
  influencers: {
    slug: "influencers",
    path: "/influencers",
    shortLabel: "Influencers",
    footerLabel: "Influencers",
    railText: "Turn attention into visible participation.",
    eyebrow: "For influencers",
    title: "Turn attention into visible participation.",
    description:
      "Run the Space, go live, make the case, and send your audience to a market where their actions show up on the board. Affiliate codes are live only after the affiliate wallet has actually voted at least once in any market. Accepted live affiliate links currently route 3% of referred vote amounts to the affiliate wallet in the vote transaction.",
    primaryCta: { label: "Browse markets to share", to: "/markets" },
    secondaryCta: { label: "View leaderboards", to: "/leaderboards" },
    intro: {
      eyebrow: "Active influence",
      title: "Give followers more to do than react.",
      body:
        "A RaizIQ market gives influencers a reason to host X Spaces, livestreams, group chats, and campaign pushes with a clear call to action: pick a side and vote."
    },
    benefits: [
      {
        title: "Create live moments",
        body: "Markets give you a reason to run Spaces, streams, and community events around a visible board."
      },
      {
        title: "Give a clear action",
        body: "Followers are not just liking or reposting. They are picking a side and voting."
      },
      {
        title: "Use affiliate links",
        body: "Share links only become live affiliate links after your affiliate wallet has actually voted at least once in any market."
      },
      {
        title: "Prove market movement",
        body: "Leaderboards and vote movement show when followers performed active tasks that cost money."
      }
    ],
    activityTitle: "Ways influencers activate a market",
    activities: [
      "Run an X Space arguing for one side.",
      "Livestream the board as votes come in.",
      "Challenge another influencer's community to move the opposite side.",
      "Post affiliate links before and during the market window after your wallet has voted at least once.",
      "Use the affiliate leaderboard as a public proof point."
    ],
    proof: {
      title: "A stronger proof point than likes.",
      body:
        "A like is easy. A vote is active. RaizIQ leaderboards show when your followers showed up, spent, voted, and moved the board."
    },
    fee: {
      eyebrow: "Affiliate fees",
      title: "3% affiliate fee on accepted referred votes.",
      body:
        "Your affiliate code is only live after your affiliate wallet has actually voted at least once in any market. You can share the link before then and players can use it to vote, but affiliate routing is not active until your own wallet has voted. When RaizIQ accepts a live affiliate parameter, the current vote flow routes 3% of that referred vote amount to the affiliate wallet in the vote transaction.",
      notes: [
        "When an accepted affiliate link is present, the affiliate wallet currently receives 3% of that referred vote amount in the vote transaction.",
        "An affiliate code is only live after the affiliate wallet has actually voted at least once in any market.",
        "Affiliate proof should be framed as visible participation and leaderboard movement.",
        "Affiliate fee routing depends on an accepted live affiliate parameter, the affiliate vote requirement, and the current vote flow."
      ]
    },
    finalCta: {
      title: "Find a market your audience can move.",
      body: "Share a live market, make the case, and use the leaderboard as proof of active participation.",
      primary: { label: "Browse markets", to: "/markets" },
      secondary: { label: "View affiliate leaders", to: "/leaderboards" }
    }
  },
  collections: {
    slug: "collections",
    path: "/collections",
    shortLabel: "Collections",
    footerLabel: "NFT collections and projects",
    railText: "Turn community activity into real support.",
    eyebrow: "For NFT collections and projects",
    title: "Turn community activity into real support.",
    description:
      "Use RaizIQ during Spaces, livestreams, game time, drops, and holder events. Create a market, select your project as the supported community, and the current vote flow can route both creator and community support fee lanes to your project.",
    primaryCta: { label: "Create a market", to: "/create" },
    secondaryCta: { label: "See community leaderboards", to: "/leaderboards" },
    intro: {
      eyebrow: "Community support",
      title: "Add a market to the activities you already run.",
      body:
        "Collections and projects can use RaizIQ during Spaces, livestreams, game time, tournaments, mint events, roadmap debates, holder calls, and community campaigns."
    },
    benefits: [
      {
        title: "Fits existing events",
        body: "Add a market to a Space, stream, game session, drop, or holder event without changing the core event format."
      },
      {
        title: "Give holders an active task",
        body: "Community members can pick a side and vote instead of only listening, chatting, or reacting."
      },
      {
        title: "Receive support-community fees",
        body: "A selected supported community currently receives a 1% community support fee from votes in that market."
      },
      {
        title: "Combine creator and support fees",
        body: "When a project creates the market and selects itself as the supported community, the current vote flow can route both fee lanes to that project."
      }
    ],
    activityTitle: "Ways projects can use a market",
    activities: [
      "Run a market during a project X Space.",
      "Create a market during game time or a tournament.",
      "Let holders choose between roadmap themes, event formats, or community challenges.",
      "Use market movement as content during a livestream.",
      "Ask partner communities to create markets that support the project."
    ],
    proof: {
      title: "Support shows up on public leaderboards.",
      body:
        "Community fee leaderboards show support collected by day, week, and all time, while market pages show the votes and activity that created that support."
    },
    fee: {
      eyebrow: "Creator plus community support",
      title: "Creator and community support fee lanes can stack.",
      body:
        "A project can receive the 3% creator fee when it creates a market and the current 1% community support fee when it is selected as the supported community. When both are true for the same market, the current vote flow can route both fee lanes to that project.",
      notes: [
        "A project receives the 3% creator fee when it creates the market.",
        "A project receives the current 1% community support fee when it is selected as the supported community.",
        "A project-created market that also selects that project as the supported community can route both fee lanes, currently 4% of voted SOL in that market.",
        "A market created by someone else can still route the 1% support fee to the selected project, but not the creator fee."
      ]
    },
    finalCta: {
      title: "Give your community a board to move.",
      body: "Create a market for your next event or ask partners to select your project as the supported community.",
      primary: { label: "Create a market", to: "/create" },
      secondary: { label: "View community leaders", to: "/leaderboards" }
    }
  }
};

export const audienceBenefitStaticRoutes = audienceBenefitOrder.map((slug) => audienceBenefitPages[slug].path);

export const audienceBenefitSeoByPath = Object.fromEntries(
  audienceBenefitOrder.map((slug) => {
    const page = audienceBenefitPages[slug];
    const titleBase = slug === "collections" ? "NFT Collections And Projects" : page.shortLabel;
    return [
      page.path,
      {
        titleBase,
        description: {
          players: "Pick a side, rally your people, and make every vote move the RaizIQ board in real time.",
          creators:
            "Create participation markets, give your audience a side to rally behind, and receive the current 3% creator fee on votes.",
          influencers:
            "Use RaizIQ markets for Spaces, livestreams, activated 3% affiliate fees, and proof that your audience takes action.",
          collections:
            "Give NFT collections and projects a participation layer for events, holder activity, and the current 1% community support fee."
        }[slug]
      }
    ];
  })
);

export function getAudienceBenefitPage(slug = "") {
  return audienceBenefitPages[String(slug || "").trim()] || null;
}

export function getAudienceBenefitPageByPath(path = "") {
  const [pathOnly] = String(path || "").split(/[?#]/);
  return audienceBenefitOrder.map((slug) => audienceBenefitPages[slug]).find((page) => page.path === pathOnly) || null;
}
